import { CodeBlock, SectionTitle, SubTitle, InfoBox } from './shared';

export default function CompactionContent() {
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-3">压缩</h1>
        <p className="text-slate-400 text-base leading-relaxed">
          压缩（Compaction）是 OpenClaw 管理上下文窗口的核心机制，将历史消息压缩为摘要以释放空间。
        </p>
      </div>

      <SectionTitle id="comp-what">什么是压缩</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-4">
        当会话消息积累到一定数量，超出模型的上下文窗口限制时，OpenClaw 会自动触发压缩。
        压缩过程会将旧的消息历史压缩成一段摘要文本，保留关键信息的同时释放上下文空间。
      </p>
      <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-4 my-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 flex items-center justify-center"><i className="ri-chat-history-line text-cyan-400 text-lg"></i></div>
          <span className="text-white font-medium text-sm">压缩前</span>
        </div>
        <div className="space-y-1 mb-4">
          {['消息 1', '消息 2', '...', '消息 80', '消息 81（当前）'].map((m, i) => (
            <div key={i} className={`text-xs px-2 py-1 rounded ${i < 4 ? 'bg-amber-500/10 text-amber-400' : 'bg-slate-700/50 text-slate-400'}`}>{m}</div>
          ))}
        </div>
        <div className="flex items-center gap-2 my-3">
          <div className="flex-1 h-px bg-slate-700"></div>
          <i className="ri-arrow-down-line text-cyan-400"></i>
          <span className="text-xs text-cyan-400">压缩</span>
          <i className="ri-arrow-down-line text-cyan-400"></i>
          <div className="flex-1 h-px bg-slate-700"></div>
        </div>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 flex items-center justify-center"><i className="ri-compress-line text-emerald-400 text-lg"></i></div>
          <span className="text-white font-medium text-sm">压缩后</span>
        </div>
        <div className="space-y-1">
          {['[摘要] 用户讨论了...（替代消息 1-80）', '消息 81（当前）'].map((m, i) => (
            <div key={i} className={`text-xs px-2 py-1 rounded ${i === 0 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-slate-700/50 text-slate-400'}`}>{m}</div>
          ))}
        </div>
      </div>

      <SectionTitle id="comp-config">配置</SectionTitle>
      <CodeBlock language="json5" code={`{
  agent: {
    compaction: {
      // 是否启用自动压缩
      auto: true,
      // 触发压缩的上下文使用率阈值（0-1）
      threshold: 0.8,
      // 压缩后保留的最近消息数
      keepRecentMessages: 10,
      // 压缩摘要的最大 token 数
      summaryMaxTokens: 500,
      // 压缩提示词（可自定义）
      summaryPrompt: "请将以下对话历史压缩为简洁的摘要，保留关键信息：",
    }
  }
}`} />

      <SectionTitle id="comp-auto">自动压缩（默认开启）</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        自动压缩默认开启，当上下文使用率超过阈值时自动触发：
      </p>
      <ul className="space-y-2 text-sm text-slate-300 mb-4">
        <li className="flex items-start gap-2.5"><i className="ri-checkbox-circle-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span>无需手动干预，完全自动化</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-checkbox-circle-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span>压缩前自动触发记忆刷新，确保重要信息不丢失</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-checkbox-circle-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span>压缩过程对用户透明，不影响对话体验</span></li>
      </ul>
      <InfoBox type="tip">
        如果你发现智能体在长对话中"忘记"了早期内容，可以适当增大 <code className="text-emerald-400 bg-slate-800/60 px-1 rounded text-xs">summaryMaxTokens</code> 值，让摘要包含更多细节。
      </InfoBox>

      <SectionTitle id="comp-manual">手动压缩</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        也可以通过命令手动触发压缩：
      </p>
      <CodeBlock language="bash" code={`# 在聊天中发送命令触发压缩
/compact

# 通过 CLI 触发压缩
openclaw compact --agent my-agent --peer telegram_user_123456`} />

      <SectionTitle id="comp-context-source">上下文窗口来源</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        上下文窗口由以下部分组成（按 token 消耗从大到小）：
      </p>
      <div className="space-y-2 my-4">
        {[
          { label: '消息历史', pct: 60, color: 'bg-cyan-500' },
          { label: '系统提示', pct: 20, color: 'bg-teal-500' },
          { label: '记忆内容', pct: 10, color: 'bg-emerald-500' },
          { label: '工具定义', pct: 7, color: 'bg-amber-500' },
          { label: '当前消息', pct: 3, color: 'bg-rose-500' },
        ].map((item) => (
          <div key={item.label} className="flex items-center gap-3">
            <div className="w-24 text-xs text-slate-400 text-right">{item.label}</div>
            <div className="flex-1 bg-slate-800 rounded-full h-2">
              <div className={`${item.color} h-2 rounded-full`} style={{ width: `${item.pct}%` }}></div>
            </div>
            <div className="w-8 text-xs text-slate-400">{item.pct}%</div>
          </div>
        ))}
      </div>

      <SectionTitle id="comp-vs-trim">压缩与修剪</SectionTitle>
      <div className="overflow-x-auto my-4">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-slate-700">
              <th className="text-left py-2.5 px-3 text-slate-400 font-medium">特性</th>
              <th className="text-left py-2.5 px-3 text-slate-400 font-medium">压缩（Compaction）</th>
              <th className="text-left py-2.5 px-3 text-slate-400 font-medium">修剪（Trimming）</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['信息保留', '生成摘要，保留关键信息', '直接删除旧消息，信息丢失'],
              ['token 消耗', '需要额外调用模型生成摘要', '无额外消耗'],
              ['适用场景', '长期对话，需要保留上下文', '短期对话，不需要历史'],
              ['触发方式', '自动或手动', '自动（超出 maxMessages）'],
            ].map(([feature, comp, trim], i) => (
              <tr key={i} className="border-b border-slate-800/60 hover:bg-slate-800/30 transition-colors">
                <td className="py-2.5 px-3 text-slate-300 font-medium">{feature}</td>
                <td className="py-2.5 px-3 text-slate-300">{comp}</td>
                <td className="py-2.5 px-3 text-slate-300">{trim}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SectionTitle id="comp-tips">提示</SectionTitle>
      <InfoBox type="tip">
        对于需要长期记忆的场景，建议同时启用压缩和向量记忆搜索。压缩保留近期上下文，向量搜索提供远期记忆检索。
      </InfoBox>
      <InfoBox type="warning">
        压缩会消耗额外的 API token（用于生成摘要）。如果 token 成本是主要考虑因素，可以适当提高压缩阈值或使用更便宜的模型生成摘要。
      </InfoBox>
    </div>
  );
}
